import cn from '@/utils/classNames'
import { type CommonProps } from '@/utils/types'
import { Slot } from '@radix-ui/react-slot'
import { type ButtonHTMLAttributes, type FC } from 'react'

import styles from './Button.module.scss'

/**
 * A reusable button component that supports multiple visual variants and can be
 * polymorphic (rendered as different HTML elements).
 *
 * @component
 * @example
 * // Default button
 * <Button onClick={handleClick}>Click me</Button>
 *
 * // Accent variant
 * <Button variant="accent">Accent Button</Button>
 *
 * // As a link using asChild
 * <Button variant="solid" asChild>
 *   <Link href="/some-page">Go to page</Link>
 * </Button>
 */
const Button: FC<ButtonProps> = (props) => {
  const { asChild, children, className, size = 'base', variant = 'default', ...rest } = props
  const Component = asChild ? Slot : 'button'
  const classList = cn(styles.button, {
    [styles.default]: !variant || variant === 'default',
    [styles.accent]: variant === 'accent',
    [styles.solid]: variant === 'solid',
    [styles.lg]: size === 'lg',
    [styles.sm]: size === 'sm'
  }, className)

  return (
    <Component className={classList} {...rest}>
      {children}
    </Component>
  )
}

export interface ButtonProps extends CommonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant of the button
   */
  variant?: 'default' | 'solid' | 'accent'

  /**
   * Button size
   */
  size?: 'base' | 'lg' | 'sm'

  /**
   * When true, renders button as a Slot component allowing it to be polymorphic
   * Useful for rendering the button as another component like Link while maintaining button styles
   */
  asChild?: boolean
}

export default Button
